import React, { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import { getSystemHealth } from "../../pages/services/healthService";
import "../../styles/SystemHealth.css";

// System health: one section per app — Admin backend, Student / Trainer
// backend, Student / Trainer frontend, Admin frontend — each with its status
// (from a live check now and the last hour's AWS numbers), the range's totals,
// and graphs over the range. Times are Indian time.

const RANGES = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["thisWeek", "This week"],
  ["last7d", "Last 7 days"], ["thisMonth", "This month"], ["last30d", "Last 30 days"],
];
const RANGE_KEY = "systemHealthRange";
const STATUS = {
  healthy: { label: "Healthy", Icon: FaCheckCircle },
  degraded: { label: "Degraded", Icon: FaExclamationTriangle },
  down: { label: "Down", Icon: FaTimesCircle },
};
// Two series per chart at most: categorical slots 1 and 2 (validated together).
const SERIES = ["var(--sh-series-1)", "var(--sh-series-2)"];

// ─── Formatting ───────────────────────────────────────────────────────────────
const IST = { timeZone: "Asia/Kolkata" };
const num = (n) => (n == null ? "—" : Math.round(n).toLocaleString("en-IN"));
const ms = (n) => (n == null ? "—" : n < 1000 ? `${Math.round(n)} ms` : `${(n / 1000).toFixed(n < 10000 ? 2 : 1)} s`);
const share = (part, whole) => (whole ? `${((part / whole) * 100).toFixed(part / whole < 0.1 ? 2 : 1)}%` : "—");
const bytes = (n) => {
  if (n == null) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i ? 1 : 0)} ${u[i]}`;
};
// Axis tick: hours for a day, day + hour for a week, the date for a month.
const tick = (bucketMs) => (t) => {
  const d = new Date(t);
  if (bucketMs <= 3600000) return d.toLocaleTimeString("en-IN", { ...IST, hour: "numeric", hour12: true }).replace(" ", "");
  if (bucketMs < 86400000) return d.toLocaleString("en-IN", { ...IST, weekday: "short", hour: "numeric", hour12: true }).replace(" ", " ");
  return d.toLocaleDateString("en-IN", { ...IST, day: "numeric", month: "short" });
};
// Tooltip heading: the bucket as a span ("4 Nov, 10 am – 11 am").
const span = (t, bucketMs) => {
  const a = new Date(t), b = new Date(t + bucketMs);
  if (bucketMs >= 86400000) return a.toLocaleDateString("en-IN", { ...IST, weekday: "short", day: "numeric", month: "short" });
  const day = a.toLocaleDateString("en-IN", { ...IST, day: "numeric", month: "short" });
  const hr = (d) => d.toLocaleTimeString("en-IN", { ...IST, hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}, ${hr(a)} – ${hr(b)}`;
};

// ─── Pieces ───────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.healthy;
  return <span className={`sh-badge is-${status}`}><s.Icon aria-hidden="true" /> {s.label}</span>;
};

const Stat = ({ label, value, sub }) => (
  <div className="sh-stat">
    <div className="sh-stat-label">{label}</div>
    <div className="sh-stat-value">{value}</div>
    {sub && <div className="sh-stat-sub">{sub}</div>}
  </div>
);

// Values lead, labels follow; every series at that point.
const ChartTooltip = ({ active, payload, label, bucketMs, format }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="sh-tip">
      <div className="sh-tip-head">{span(label, bucketMs)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="sh-tip-row">
          <i style={{ background: p.color }} aria-hidden="true" />
          <b>{format(p.value)}</b> <span>{p.name}</span>
        </div>
      ))}
    </div>
  );
};

// One chart: a single series as a soft area, two as lines with a legend.
// series: [{ key, name }]; one y-axis, one unit per chart.
const Chart = ({ title, points, series, bucketMs, format, empty }) => {
  const hasData = points.some((p) => series.some((s) => p[s.key] != null && p[s.key] !== 0));
  const common = (
    <>
      <CartesianGrid stroke="var(--sh-grid)" vertical={false} />
      <XAxis dataKey="at" tickFormatter={tick(bucketMs)} tick={{ fill: "var(--sh-muted)", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "var(--sh-line)" }} minTickGap={18} />
      <YAxis tickFormatter={format} tick={{ fill: "var(--sh-muted)", fontSize: 11 }} tickLine={false} axisLine={false} width={64} allowDecimals={false} />
      <Tooltip content={<ChartTooltip bucketMs={bucketMs} format={format} />} cursor={{ stroke: "var(--sh-muted)", strokeWidth: 1 }} />
    </>
  );
  return (
    <figure className="sh-chart">
      <figcaption>
        <span className="sh-chart-title">{title}</span>
        {series.length > 1 && (
          <span className="sh-legend">
            {series.map((s, i) => <span key={s.key}><i style={{ background: SERIES[i] }} aria-hidden="true" />{s.name}</span>)}
          </span>
        )}
      </figcaption>
      {!hasData ? <div className="sh-chart-empty">{empty || "Nothing recorded in this period."}</div> : (
        <ResponsiveContainer width="100%" height={200}>
          {series.length === 1 ? (
            <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              {common}
              <Area type="monotone" dataKey={series[0].key} name={series[0].name} stroke={SERIES[0]} strokeWidth={2} fill={SERIES[0]} fillOpacity={0.12} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            </AreaChart>
          ) : (
            <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              {common}
              {series.map((s, i) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={SERIES[i]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </figure>
  );
};

// The same numbers as a table, for reading exact values.
const DataTable = ({ points, columns, bucketMs }) => (
  <details className="sh-table">
    <summary>Show as a table</summary>
    <div className="sh-table-wrap">
      <table>
        <thead><tr><th>Period</th>{columns.map((c) => <th key={c.key}>{c.name}</th>)}</tr></thead>
        <tbody>
          {points.map((p) => <tr key={p.at}><td>{span(p.at, bucketMs)}</td>{columns.map((c) => <td key={c.key}>{c.format(p[c.key])}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  </details>
);

const LiveChecks = ({ live }) => (
  <div className="sh-live">
    <span className="sh-live-title">Live check</span>
    {live.map((l) => (
      <span key={l.url} className={`sh-live-item ${l.up ? (l.slow ? "is-slow" : "is-up") : "is-down"}`} title={l.url}>
        {l.up ? <FaCheckCircle aria-hidden="true" /> : <FaTimesCircle aria-hidden="true" />}
        <b>{l.label}</b>
        <span>{l.up ? `answered in ${ms(l.ms)}` : l.error || `HTTP ${l.status}`}{l.status && l.up ? ` · HTTP ${l.status}` : ""}</span>
      </span>
    ))}
  </div>
);

const AppSection = ({ app, range }) => {
  const b = range.bucketMs;
  const t = app.totals || {};
  return (
    <section className="sh-app" aria-labelledby={`sh-${app.key}`}>
      <header className="sh-app-head">
        <div>
          <h2 id={`sh-${app.key}`}>{app.name}</h2>
          <div className="sh-hosting">{app.hosting}</div>
        </div>
        <StatusBadge status={app.status} />
      </header>

      {app.reasons.length > 0 && (
        <ul className="sh-reasons">{app.reasons.map((r, i) => <li key={i} className={`is-${r.level}`}>{r.text}</li>)}</ul>
      )}
      <LiveChecks live={app.live} />

      {app.metricsError && <div className="sh-alert">{app.metricsError} Until then only the live check is shown.</div>}

      {app.kind === "backend" && app.points && (
        <>
          <div className="sh-stats">
            <Stat label="Requests" value={num(t.requests)} />
            <Stat label="Server errors (5xx)" value={num(t.serverErrors)} sub={`${share(t.serverErrors, t.requests)} of requests`} />
            <Stat label="Client errors (4xx)" value={num(t.clientErrors)} sub={`${share(t.clientErrors, t.requests)} · logins, bad input`} />
            <Stat label="Average response" value={ms(t.avgMs)} sub={`slowest 5%: ${ms(t.p95Ms)}`} />
            <Stat label="Crashes" value={num(t.crashes)} sub={`${share(t.crashes, t.invocations)} of ${num(t.invocations)} runs`} />
            <Stat label="Throttled" value={num(t.throttles)} sub="turned away by AWS" />
            <Stat label="Peak concurrency" value={num(t.peakConcurrency)} sub={`longest run ${ms(t.maxDurationMs)}`} />
          </div>
          <div className="sh-charts">
            <Chart title="Requests" points={app.points} series={[{ key: "requests", name: "Requests" }]} bucketMs={b} format={num} />
            <Chart title="Errors" points={app.points} series={[{ key: "serverErrors", name: "Server errors (5xx)" }, { key: "crashes", name: "Crashes" }]} bucketMs={b} format={num} empty="No server errors or crashes in this period." />
            <Chart title="Response time" points={app.points} series={[{ key: "avgMs", name: "Average" }, { key: "p95Ms", name: "Slowest 5%" }]} bucketMs={b} format={ms} />
          </div>
          <DataTable points={app.points} bucketMs={b} columns={[
            { key: "requests", name: "Requests", format: num }, { key: "serverErrors", name: "5xx", format: num },
            { key: "clientErrors", name: "4xx", format: num }, { key: "crashes", name: "Crashes", format: num },
            { key: "throttles", name: "Throttled", format: num }, { key: "avgMs", name: "Average", format: ms }, { key: "p95Ms", name: "Slowest 5%", format: ms },
          ]} />
        </>
      )}

      {app.kind === "frontend" && app.points && (
        <>
          <div className="sh-stats">
            <Stat label="Page & file requests" value={num(t.requests)} />
            <Stat label="Server errors (5xx)" value={num(t.serverErrors)} sub={`${share(t.serverErrors, t.requests)} of requests`} />
            <Stat label="Not found / denied (4xx)" value={num(t.clientErrors)} sub={`${share(t.clientErrors, t.requests)} of requests`} />
            <Stat label="Data served" value={bytes(t.bytes)} />
          </div>
          <div className="sh-charts">
            <Chart title="Page & file requests" points={app.points} series={[{ key: "requests", name: "Requests" }]} bucketMs={b} format={num} />
            <Chart title="Errors" points={app.points} series={[{ key: "serverErrors", name: "Server errors (5xx)" }, { key: "clientErrors", name: "Not found / denied (4xx)" }]} bucketMs={b} format={num} empty="No errors in this period." />
          </div>
          <DataTable points={app.points} bucketMs={b} columns={[
            { key: "requests", name: "Requests", format: num }, { key: "serverErrors", name: "5xx", format: num },
            { key: "clientErrors", name: "4xx", format: num }, { key: "bytes", name: "Data served", format: bytes },
          ]} />
        </>
      )}

      {app.note && app.points && <p className="sh-note">{app.note}</p>}
    </section>
  );
};

// ─── The page ─────────────────────────────────────────────────────────────────
const SystemHealth = () => {
  const [range, setRange] = useState(() => {
    try { const r = localStorage.getItem(RANGE_KEY); return RANGES.some(([k]) => k === r) ? r : "today"; } catch { return "today"; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback((fresh = false) => {
    setLoading(true);
    setError("");
    getSystemHealth(range, { fresh })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);
  // While the page is open, check again every 2 minutes.
  useEffect(() => { const t = setInterval(() => load(true), 120000); return () => clearInterval(t); }, [load]);

  const pick = (k) => { setRange(k); try { localStorage.setItem(RANGE_KEY, k); } catch { /* private window */ } };

  return (
    <div className="sh-page">
      <div className="sh-head">
        <div>
          <h1>System health</h1>
          <p>Each app's status now, and its AWS numbers over time (Indian time).{data && ` Checked ${new Date(data.checkedAt).toLocaleTimeString("en-IN", { ...IST, hour: "numeric", minute: "2-digit", hour12: true })}.`}</p>
        </div>
        <button type="button" className="sh-btn" onClick={() => load(true)} disabled={loading}>{loading ? "Checking…" : "↻ Refresh"}</button>
      </div>

      <div className="sh-ranges" role="group" aria-label="Date range">
        {RANGES.map(([k, label]) => (
          <button key={k} type="button" className={`sh-range${range === k ? " is-active" : ""}`} aria-pressed={range === k} onClick={() => pick(k)}>{label}</button>
        ))}
      </div>

      {error && <div className="sh-alert is-error">{error}</div>}

      {data && (
        <div className="sh-overview">
          {data.apps.map((a) => (
            <a key={a.key} href={`#sh-${a.key}`} className={`sh-overview-item is-${a.status}`}>
              <span>{a.name}</span>
              <StatusBadge status={a.status} />
            </a>
          ))}
        </div>
      )}

      {!data && loading && <div className="sh-loading">Checking every app…</div>}
      {data && data.apps.map((a) => <AppSection key={a.key} app={a} range={data.range} />)}

      {data && (
        <details className="sh-rules">
          <summary>How the status is decided</summary>
          <ul>
            <li><b>Down</b> — the app's address does not answer, or answers with a server error.</li>
            <li><b>Degraded</b> — in the last hour: over {data.limits.serverErrorRate * 100}% of backend requests failed with a server error, over {data.limits.crashRate * 100}% of runs crashed, any request was throttled, the slowest 5% took over {data.limits.slowP95Ms / 1000} s, or over {data.limits.siteServerErrorRate * 100}% of site requests failed; or a live check took over {data.limits.liveSlowMs / 1000} s. Error rates are judged only with at least {data.limits.minRequests} requests in the hour.</li>
            <li><b>Healthy</b> — none of these.</li>
          </ul>
          <p>AWS numbers arrive 1–3 minutes late. The page checks again every 2 minutes while it is open.</p>
        </details>
      )}
    </div>
  );
};

export default SystemHealth;
